import "./ai-consent-shared.scss";

import {
  Chip,
  ChipBackgroundColor,
  ChipSize,
} from "@amzn/stencil-react-components/chip";
import { FormattedMessage } from "react-intl";

import aivaIconSmall from "../../assets/icons/aivaIconSmall.svg";

interface AiAutomationChipProps {
  /** Test id prefix. The chip takes `<prefix>Chip` and the Aiva mark inside takes `<prefix>Mark`. */
  readonly testIdPrefix: string;
  /**
   * Whether to keep the chip out of the accessibility tree.
   *
   * Set where the chip only repeats something a nearby element already announces, which is the
   * case on a surface that cannot nest it inside the thing it labels.
   */
  readonly isDecorative?: boolean;
}

/**
 * The "AI automation" chip, in the design's AI gradient.
 *
 * One component for every surface that marks a step as automatable, so the gradient, the Aiva mark
 * and the wording cannot drift apart between them.
 *
 * The gradient is applied in `ai-consent-shared.scss` rather than here: no ChipBackgroundColor
 * carries it, and Chip takes no className, so the stylesheet reaches it through the wrapper. The
 * stylesheet is imported here so the chip carries its own painting to every surface, rather than
 * relying on whichever screen happens to have imported it.
 *
 * One painting for both themes. The design gives the chip the same "AI light" fill on its dark screens
 * as on its light ones, so nothing here reads the theme.
 */
export const AiAutomationChip = ({
  testIdPrefix,
  isDecorative = false,
}: AiAutomationChipProps): JSX.Element => {
  return (
    <span
      className="ai-automation-chip"
      aria-hidden={isDecorative || undefined}
      data-test-id={`${testIdPrefix}ChipWrapper`}
    >
      <Chip
        size={ChipSize.ExtraSmall}
        backgroundColor={ChipBackgroundColor.Default}
        icon={
          <img
            /*
             * Always the dark mark. The chip keeps the light AI gradient in both themes, as the design
             * does, so the white mark would be near-invisible on it.
             */
            src={aivaIconSmall}
            alt=""
            className="ai-consent-chip-mark"
            data-test-id={`${testIdPrefix}Mark`}
          />
        }
        dataTestId={`${testIdPrefix}Chip`}
      >
        <FormattedMessage id="aiConsent-ai-automation-chip" />
      </Chip>
    </span>
  );
};
